<template>
  <Loading v-if="loading" />
  <div class="orderPayment">
    <FormKit
      id="orderPayment-form"
      v-model="formData"
      type="form"
      :actions="false"
      @submit="submitHandler"
    >
      <div class="orderPayment__actionsTaken">
        <div class="orderPayment__title">
          <p class="orderPayment__title-text">
            {{ $t('orderPayment') }}
          </p>
        </div>
        <div class="orderPayment__checkboxes">
          <Checkbox
            name="prescription"
            :value="prescription"
            :title="$t('prescription')"
            @change="prescription = !prescription"
          />
          <Checkbox
            name="other"
            :value="other"
            :title="$t('other')"
            @change="other = !other"
          />
        </div>
      </div>
      <div class="orderPayment__order">
        <div class="orderPayment__title">
          <p class="orderPayment__title-text">
            {{ $t('medicationDelivery') }}
          </p>
        </div>
        <div class="orderPayment__checkboxes">
          <Checkbox
            name="prescriptionDrugs"
            :value="prescriptionDrugs"
            :title="$t('prescriptionDrugs')"
            @change="changePrescriptionDrugs()"
          />
          <Checkbox
            name="selfCare"
            :value="selfCare"
            :title="$t('selfCare')"
            @change="changeSelfCare()"
          />
        </div>
        <div v-if="prescriptionDrugs || selfCare" class="isOrder">
          <div class="orderPayment__checkboxes">
            <p class="orderPayment-title">
              {{ $t('medicalAdviceUpperCase') }}
            </p>
            <div :class="{ tooltip: !formData.consultation }">
              <Checkbox
                name="consultation"
                :value="formData.consultation"
                :title="$t('medicalAdviceGiven')"
                @change="formData.consultation = !formData.consultation"
              />
              <span v-if="!formData.consultation" class="tooltiptext">
                {{ $t('rememberAboutMedicalAdvice') }}
              </span>
            </div>
          </div>
          <div class="orderPayment__numberAndComment">
            <Input
              class="input-reference"
              type="text"
              name="orderNumber"
              :value="formData.orderNumber"
              :label="$t('orderNumber')"
            />
            <div class="comment">
              <label class="label-comment" for="comment">
                {{ $t('internalNote') }}
              </label>
              <textarea
                id="comment"
                v-model="formData.comment"
                class="input-comment"
                maxlength="250"
              >
              </textarea>
            </div>
          </div>
          <div class="orderPayment__container">
            <p class="orderPayment-title">
              {{ $t('deliveryMethodUpperCase') }}
            </p>
            <div class="orderPayment__deliveryMethod-radio">
              <Radio
                :name="'pharmacy'"
                :title="$t('pharmacy')"
                :value="formData.deliveryMethod === 'pharmacy'"
                @change="updateDeliveryMethod('pharmacy')"
              />
              <Radio
                :name="'pickupbox'"
                :title="$t('pickupBox')"
                :value="formData.deliveryMethod === 'pickupbox'"
                @change="updateDeliveryMethod('pickupbox')"
              />
              <Radio
                :name="'homeDelivery'"
                :value="formData.deliveryMethod === 'homeDelivery'"
                :title="$t('homeDelivery')"
                @change="updateDeliveryMethod('homeDelivery')"
              />
            </div>
          </div>
        </div>
      </div>
    </FormKit>
    <div v-if="prescriptionDrugs || selfCare">
      <div v-if="!shouldHidePayment" class="orderPayment__container">
        <p class="orderPayment-title">
          {{ $t('issuePaymentUpperCase') }}
        </p>
        <Checkbox
          name="issuePayment"
          :value="issuePayment"
          :title="$t('issuePayment')"
          @change="changeIssuePayment"
        />
      </div>
    </div>
    <IssuePayment
      :issue-payment="issuePayment"
      :order-payment="orderPayment"
      @update:issue-payment-form-data="updateIssuePaymentFormData"
    />
    <span v-if="noTotalSum" class="noTotalSum">
      {{ $t('fieldIsIncorrectly') }}
    </span>
    <div class="orderPayment__buttons">
      <div class="saveForm-buttons">
        <Button
          :variant="isSaveForm ? 'disabledBackground' : 'filled'"
          :title="$t('saveForm')"
          @click="saveForm"
        />
        <Button
          v-if="openButton"
          class="authCheck"
          :variant="getButtonPayment()"
          :title="$t(getButtonPayment())"
        />
      </div>
      <div v-if="issuePayment && isSaveForm" class="sendPayment-buttons">
        <Button
          variant="filled"
          :title="$t('sendSMSPayment')"
          @click="sendSMSPayment"
        />
        <Button
          v-if="!isBankId"
          variant="filled"
          :title="$t('sendPayment')"
          @click="sendPayment"
        />
      </div>
    </div>

    <Footer
      :is-save-form="isSaveForm"
      @close-discard-authentication="closeDiscardAuthentication"
      @finish-authentication="finishAuthentication"
    />
    <DiscardAuthentication
      :reference="reference"
      :show-modal-discard-authentication="showModalDiscardAuthentication"
      @discard-authentication="discardAuthentication"
      @close-modal="showModalDiscardAuthentication = false"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';
import { getDatabase, ref, onValue } from 'firebase/database';

import Loading from '@/views/Loading.vue';
import IssuePayment from './IssuePayment/IssuePayment.vue';
import DiscardAuthentication from './DiscardAuthentication/DiscardAuthentication.vue';

import {
  authenticationNameInfo,
  authenticationSsnAndAddressInfo
} from '@/constants';

import {
  OrderPayment,
  UpdateIssuePaymentFormData
} from '@/store/modules/orders/types';

import { getTotalSum } from '@/utils/getTotalSum';

@Options({
  name: 'PaymentDetails',
  props: [
    'userId',
    'isBankId',
    'authRecordId',
    'reference',
    'realTimeValueSMS'
  ],
  emits: ['clearAllForms'],
  components: {
    Loading,
    IssuePayment,
    DiscardAuthentication
  },
  data() {
    return {
      formData: {
        consultation: false,
        deliveryMethod: '',
        orderType: [],
        orderNumber: '',
        comment: '',
        callReason: []
      },
      loading: false,
      issuePayment: false,
      prescription: false,
      other: false,
      prescriptionDrugs: false,
      selfCare: false,
      newIssuePaymentFormData: {},
      authenticationNameInfo,
      authenticationSsnAndAddressInfo,
      showModalDiscardAuthentication: false,
      isSaveForm: false,
      openButton: false,
      noTotalSum: false,
      realTimeValue: {},
      isPayments: false
    };
  },
  computed: {
    ...mapState('orders', ['orderPayment']),
    ...mapState('account', ['user']),
    ...mapState('pharmacy', ['pharmacy']),
    shouldHidePayment() {
      return (
        this.realTimeValueSMS && this.isEmptyObject(this.pharmacy?.payments)
      );
    }
  },
  methods: {
    ...mapActions('orders', [
      'getOrderCreate',
      'getIssuePaymentPush',
      'getIssuePaymentSMSPush'
    ]),
    // eslint-disable-next-line
    isEmptyObject(obj: any) {
      return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
    },
    changeIssuePayment() {
      this.issuePayment = !this.issuePayment;
    },
    changePrescriptionDrugs() {
      this.prescriptionDrugs = !this.prescriptionDrugs;
    },
    changeSelfCare() {
      this.selfCare = !this.selfCare;
    },
    updateDeliveryMethod(method: string) {
      this.deliveryMethod = method;
      this.formData.deliveryMethod = method;
    },
    getButtonPayment() {
      switch (this.realTimeValue.paymentInfo.status) {
        case 'pending':
          return 'paymentPending';
        case 'failed':
          return 'paymentFailed';
        case 'cancelled':
          return 'cancelled';
        default:
          return 'paymentCompleted';
      }
    },
    updateIssuePaymentFormData(formData: UpdateIssuePaymentFormData) {
      this.newIssuePaymentFormData = {
        ...formData,
        userId: this.userId,
        paymentInfo: {
          reference: formData.paymentInfo.reference,
          totalSum: +getTotalSum(formData.paymentInfo.totalSum),
          comment: formData.paymentInfo.comment
        }
      };

      delete this.newIssuePaymentFormData.reference;
      delete this.newIssuePaymentFormData.totalSum;
    },
    async submitHandler(data: OrderPayment) {
      if (
        this.issuePayment &&
        (!this.newIssuePaymentFormData.paymentInfo.totalSum ||
          +this.newIssuePaymentFormData.paymentInfo.totalSum < 0.01)
      ) {
        this.noTotalSum = true;
      } else {
        this.loading = true;
        const payload = {
          ...data,
          userId: this.userId,
          authRecordId: this.authRecordId,
          callReason: [
            ...(data.other ? ['other'] : []),
            ...(data.prescription ? ['prescription'] : [])
          ],
          orderType: [
            ...(data.selfCare ? ['selfCare'] : []),
            ...(data.prescriptionDrugs ? ['prescriptionDrugs'] : [])
          ]
        };

        delete payload.selfCare;
        delete payload.prescriptionDrugs;
        delete payload.prescription;
        delete payload.other;

        await this.getOrderCreate(payload);

        if (this.issuePayment) {
          this.noTotalSum = false;
        }

        this.isSaveForm = true;
        this.loading = false;
      }
    },
    async fetchPaymentStatus() {
      const database = getDatabase();

      const pharmacyId = this.user.pharmacyId;
      const orderId = this.orderPayment.orderId;

      const baseUrl = `/orders/${pharmacyId}/${orderId}`;

      const dbRef = ref(database, baseUrl);

      onValue(dbRef, (snapshot) => {
        this.realTimeValue = snapshot.val();

        if (this.realTimeValue.paymentInfo.status) {
          this.openButton = true;
        }
      });
    },
    async sendPayment() {
      this.loading = true;

      const payload = {
        ...this.newIssuePaymentFormData,
        sendPush: true,
        orderId: this.orderPayment.orderId
      };

      await this.getIssuePaymentPush(payload);
      this.fetchPaymentStatus();

      this.loading = false;

      if (!this.orderPushNotification) {
        this.authenticationFailed = true;
      } else {
        this.authenticationFailed = false;
      }
    },
    async sendSMSPayment() {
      this.loading = true;

      const payload = {
        ...this.newIssuePaymentFormData,
        orderId: this.orderPayment.orderId
      };

      await this.getIssuePaymentSMSPush(payload);
      this.fetchPaymentStatus();

      this.loading = false;

      if (!this.orderPushNotification || !this.orderSMSPushNotification) {
        this.authenticationFailed = true;
      } else {
        this.authenticationFailed = false;
      }
    },
    saveForm() {
      this.loading = true;

      if (!this.isSaveForm) {
        this.$formkit.submit('orderPayment-form');
      }

      this.loading = false;
    },
    finishAuthentication() {
      this.$formkit.reset('orderPayment-form');
      this.$emit('clearAllForms');
    },
    closeDiscardAuthentication() {
      this.showModalDiscardAuthentication = true;
    },
    discardAuthentication() {
      this.showModalDiscardAuthentication = false;
      this.$emit('clearAllForms');
    }
  },
  watch: {
    newIssuePaymentFormData() {
      if (
        this.newIssuePaymentFormData.paymentInfo.totalSum ||
        +this.newIssuePaymentFormData.paymentInfo.totalSum > 0.01
      ) {
        this.noTotalSum = false;
      } else {
        this.noTotalSum = true;
      }
    }
  }
})
export default class PaymentDetails extends Vue {}
</script>

<style lang="scss" scoped>
.orderPayment {
  margin-bottom: 144px;
  .orderPayment__title {
    position: relative;
    width: 100%;
    margin-top: 50px;
    margin-bottom: 34px;
    margin-left: 26px;
    .orderPayment__title-text {
      font-family: 'Ubuntu-Medium';
      font-size: $sixteenSize;
      color: $main-text;
    }
    &::after {
      content: '';
      position: absolute;
      top: 9px;
      left: 180px;
      width: 100%;
      height: 1px;
      background: $ligher-grey;
    }
    &::before {
      content: '';
      position: absolute;
      top: 7px;
      left: 175px;
      width: 5px;
      height: 5px;
      border-radius: 8px;
      background: $ligher-grey;
    }
  }
  .orderPayment__container {
    margin-top: 28px;
    margin-left: 26px;
    .orderPayment__deliveryMethod-radio {
      display: flex;
      align-items: center;
    }
  }
  .orderPayment-title {
    font-size: $tenSize;
    font-family: 'Ubuntu-Medium';
    color: $columnMainTitle;
    padding-bottom: 26px;
  }
  #orderPayment-form {
    .orderPayment__checkboxes {
      margin-top: 30px;
      margin-left: 26px;
    }
    .orderPayment__numberAndComment {
      margin-top: 30px;
      margin-left: 26px;
      .comment {
        position: relative;
        label {
          position: absolute;
          font-family: 'Ubuntu-Regular';
          top: -6px;
          left: 15px;
          padding: 0 5px;
          background: $white;
          color: $label;
          font-weight: 400;
          font-size: $twelveSize;
          line-height: $fourteenSize;
          z-index: 1;
        }
        textarea {
          width: 445px;
          height: 132px;
          padding: 16px;
          font-size: $fourteenSize;
          border: 1px solid $border;
          border-radius: 8px;
          resize: none;
          &:focus {
            outline: none;
          }
        }
      }
    }
    .checkbox {
      margin-bottom: 14px;
    }
  }
  .noTotalSum {
    font-size: $twelveSize;
    color: $red;
    margin-left: 26px;
  }
  .orderPayment__buttons {
    display: flex;
    align-items: flex-start;
    margin-top: 24px;
    margin-left: 26px;
    flex-direction: column;
    .saveForm-buttons {
      display: flex;
      .disabledBackgroundLong {
        width: 230px;
      }
      .authCheck {
        width: 100%;
        min-width: 153px;
        max-width: 187px;
      }
      .button {
        margin-right: 10px;
        &:first-child {
          margin-left: 0;
        }
      }
    }
    .sendPayment-buttons {
      display: flex;
      margin-top: 24px;
      .button {
        margin-right: 10px;
        &:first-child {
          margin-left: 0;
        }
      }
    }
  }
}

.tooltip {
  position: relative;
  width: max-content;
  .tooltiptext {
    position: absolute;
    top: -30%;
    left: 50%;
    transform: translate(-50%, -100%);
    visibility: hidden;
    width: 180px;
    font-size: $twelveSize;
    line-height: $twelveSize;
    background: $red;
    color: $white;
    text-align: center;
    border-radius: 6px;
    padding: 5px;
    z-index: 1;
    opacity: 0;
    transition: opacity 0.3s;
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      margin-left: -5px;
      border-width: 5px;
      border-style: solid;
      border-color: $red transparent transparent transparent;
    }
  }
  &:hover .tooltiptext {
    visibility: visible;
    opacity: 1;
  }
}
</style>
